import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, LayoutDashboard, ListTodo, Terminal, GitBranch, Copy, Check, ChevronRight,
  ChevronDown, AlertTriangle, Hand, CircleDot, Plus, ExternalLink, Loader2, Hash,
  HeartPulse, Activity, Cpu,
} from 'lucide-react';
import { usePanelOverlay } from '@/stores/uiStore';
import { api } from '@/services/api';
import clsx from 'clsx';

// ── Types (mirror /api/v1/devpanel/* responses) ──────────────────────────────
interface Flag { key: string; value: boolean; source: string }
interface Activity { instance_id: string; project?: string | null; branch?: string | null; last_title?: string | null; turn_status?: string | null; relative_time?: string | null }
interface Overview {
  generated_at?: string;
  flags: Flag[];
  recent_activity: Activity[];
  curated: any;
  sources: Record<string, string>;
}
interface Instance {
  instance_id: string; project?: string | null; branch?: string | null;
  last_title?: string | null; last_turn_status?: string | null;
  last_timestamp?: string | null; relative_time?: string | null; turn_count: number;
  liveness?: string | null; // active | idle | stale
}
interface SoakUser {
  user: string; all_gates_pass: boolean; breaches: string[];
  // Gates that could not be evaluated. Not passes, and not breaches either.
  unevaluated?: string[];
  budget_utilization?: number | null; deadman_killed?: boolean | null;
  // null means the evidence source was unreadable — NOT that the count was zero.
  candidates?: number | null; delivered?: number | null;
  unreadable_delivery_sources?: string[];
}
interface Soak {
  feature: string; days_total: number; days_remaining: number; pct_complete: number;
  through?: string | null; expired: boolean; all_gates_pass: boolean; users: SoakUser[];
}
interface HealthCheck {
  name: string; status: 'pass' | 'degraded' | 'fail' | 'skip';
  detail?: string; latency_ms?: number; checked_at?: string | null;
}
interface FeatureHealth {
  overall: string; completed_at?: string | null;
  pass_count: number; degraded_count: number; fail_count: number; skip_count: number;
  checks: HealthCheck[];
}
interface FitnessEntry {
  run_id: string; adapter_id: string; date?: string | null; composite: number;
  dimensions: Record<string, number | null>;
  merged?: boolean | null; hard_gate_pass?: boolean | null;
  decision_reason?: string | null; data_window?: string | null;
}
// Djo Instruments — /api/v1/djo-eval/dashboard (the 14 Butlin/Long indicator-property scores)
interface DjoIndicator { level: number; A: number; F: number; inherited?: boolean; capped_at?: number | null; notes?: string[] }
interface DjoDashboard {
  status?: string; rubric_version?: string; vector: number[];
  indicator_property_score: number; out_of: number;
  indicators: Record<string, DjoIndicator>; inherited?: string[];
  autonomy?: Record<string, number | null>;
  armings?: { flag: string; service?: string; new?: string; ts?: string | null }[];
  calibration?: { agreement?: number | null; valid?: boolean; min_required?: number };
  contamination?: { ok?: boolean | null; total?: number };
  uptime?: { mean_run_completion?: number | null; runs?: number; target?: number };
}
interface MiniService {
  status: 'healthy' | 'down' | 'degraded' | 'unconfigured';
  latency_ms?: number; error?: string; model?: string;
}
interface MacMiniStatus {
  reachable: boolean;
  services: Record<string, MiniService>;
  checked_at?: string;
  from_cache?: boolean;
}

// Feature-check status -> dot colour.
function healthTone(s: string): string {
  if (s === 'pass') return '#34d399';   // green
  if (s === 'degraded') return '#fbbf24'; // yellow
  if (s === 'fail') return '#ff3a3a';    // red
  return '#6b7280';                       // skip -> gray
}

// active = live now, idle = quiet, stale = probably gone.
function livenessTone(l?: string | null): { color: string; label: string } {
  if (l === 'active') return { color: '#34d399', label: 'live' };
  if (l === 'idle') return { color: '#fbbf24', label: 'idle' };
  return { color: '#6b7280', label: 'stale' };
}
function miniServiceTone(s?: string): string {
  if (s === 'healthy') return '#34d399';
  if (s === 'degraded') return '#fbbf24';
  if (s === 'down') return '#ff3a3a';
  return '#6b7280';
}
const MINI_SERVICE_LABELS: Record<string, string> = {
  agent: 'agent', pcc: 'PCC', system: 'fm-sys',
  chatterbox: 'TTS', embedding: 'embed', owned_model: 'LoRA',
};
interface Turn {
  id: string; instance_id: string; cwd?: string | null; branch?: string | null;
  project?: string | null; turn_status?: string | null; summary: string;
  title?: string | null; commit_sha?: string | null; files_changed: string[];
  created_at: string; relative_time?: string | null;
}
interface Todo {
  id: string; item: string; context?: string | null; status: string;
  source?: string | null; created_at: string; relative_time?: string | null; done_at?: string | null;
}
interface Blocked {
  instance_id: string; project?: string | null; branch?: string | null;
  last_title?: string | null; question: string; relative_time?: string | null;
}

const _DEV_CLIP = 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))';
const POLL_MS = 20000;
const GITHUB_OWNER = 'carterhamm';
const FLIP_FLAGS = ['STANDPOINT_LIVE_ENABLED', 'EXPERIENCE_ENABLED', 'EXPERIENCE_SURFACE_ENABLED', 'AGENCY_ENABLED'];

// ── tone helpers ──────────────────────────────────────────────────────────────
function statusTone(s?: string | null): { color: string; bg: string } {
  const v = (s || '').toLowerCase();
  if (/(live|deployed|online|merged\b|ready)/.test(v)) return { color: '#39ff14', bg: 'rgba(57,255,20,0.12)' };
  if (/(awaiting|wait|in-progress|pending|mute)/.test(v)) return { color: '#ffbf00', bg: 'rgba(255,191,0,0.12)' };
  if (/(error|fail|broke|down|crash)/.test(v)) return { color: '#ff3a3a', bg: 'rgba(255,58,58,0.12)' };
  if (/(dark|merged-dark|standby)/.test(v)) return { color: '#00d4ff', bg: 'rgba(0,212,255,0.10)' };
  return { color: '#6b7280', bg: 'rgba(120,130,150,0.10)' }; // planned / design-only / unknown
}
function sourceTone(s?: string | null): string {
  const v = (s || '').toLowerCase();
  if (v.includes('chat')) return '#a855f7';
  if (v.includes('carter')) return '#00d4ff';
  if (v.includes('instance')) return '#00f0ff';
  return '#6b7280';
}

function Pill({ label, tone }: { label: string; tone: { color: string; bg: string } }) {
  return (
    <span
      className="px-2 py-0.5 text-[8px] font-mono uppercase tracking-wider hud-clip-sm"
      style={{ color: tone.color, background: tone.bg, border: `1px solid ${tone.color}33` }}
    >
      {label}
    </span>
  );
}

function CopyBtn({ text, title = 'Copy' }: { text: string; title?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1200); }).catch(() => {});
      }}
      className="glass-circle w-6 h-6 flex items-center justify-center flex-shrink-0 hover:!border-jarvis-blue/40 transition-colors"
    >
      {done ? <Check size={11} className="text-hud-green" /> : <Copy size={11} className="text-gray-500" />}
    </button>
  );
}

// ── Home tab ──────────────────────────────────────────────────────────────────
function HomeTab({ overview, instances, blocked, soaks, health, miniStatus, fitness, djo, openTodos, onOpenInstance, onGoTodo }: {
  overview: Overview | null; instances: Instance[]; blocked: Blocked[]; soaks: Soak[];
  health: FeatureHealth | null; miniStatus: MacMiniStatus | null; fitness: FitnessEntry[];
  djo: DjoDashboard | null; openTodos: number;
  onOpenInstance: (id: string) => void; onGoTodo: () => void;
}) {
  const pillars: any[] = overview?.curated?.pillars || [];
  const tiers: any[] = overview?.curated?.audit_tiers || [];
  const flags = overview?.flags || [];
  const flip = [...flags].sort((a, b) => {
    const ai = FLIP_FLAGS.indexOf(a.key), bi = FLIP_FLAGS.indexOf(b.key);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || a.key.localeCompare(b.key);
  });

  return (
    <div className="space-y-5">
      {/* Waiting on you */}
      {blocked.length > 0 && (
        <Section icon={Hand} title="Waiting on you" accent="#ffbf00" count={blocked.length}>
          <div className="space-y-1.5">
            {blocked.map((b) => (
              <button
                key={b.instance_id}
                onClick={() => onOpenInstance(b.instance_id)}
                className="w-full text-left glass-subtle hud-clip-sm px-3 py-2 flex items-start gap-2.5 hover:!border-hud-amber/40 transition-colors"
                style={{ borderLeft: '2px solid #ffbf00' }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-200 font-mono truncate">{b.instance_id}</span>
                    {b.project && <Pill label={b.project} tone={statusTone('dark')} />}
                  </div>
                  <p className="text-[11px] text-hud-amber/90 mt-0.5 line-clamp-2">{b.question || b.last_title}</p>
                </div>
                <span className="text-[8px] font-mono text-gray-600 whitespace-nowrap mt-0.5">{b.relative_time}</span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Flip state */}
      <Section icon={CircleDot} title="Flip state" sub="live flags">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {flip.length === 0 && <span className="text-[10px] text-gray-600">No flags reported.</span>}
          {flip.map((f) => (
            <div key={f.key} className="glass-subtle hud-clip-sm px-2.5 py-1.5 flex items-center justify-between gap-1">
              <span className="text-[8.5px] font-mono text-gray-400 truncate" title={f.key}>
                {f.key.replace(/_ENABLED$/, '').replace(/_/g, ' ')}
              </span>
              <span className="flex items-center gap-1 flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: f.value ? '#39ff14' : '#374151', boxShadow: f.value ? '0 0 6px #39ff14' : 'none' }} />
                <span className="text-[8px] font-mono" style={{ color: f.value ? '#39ff14' : '#6b7280' }}>{f.value ? 'ON' : 'OFF'}</span>
              </span>
            </div>
          ))}
        </div>
      </Section>

      {/* Sprint progress */}
      {pillars.length > 0 && (
        <Section icon={LayoutDashboard} title="Sprint pillars">
          <div className="space-y-1.5">
            {pillars.map((p, i) => {
              const tone = statusTone(p.status);
              return (
                <div key={i} className="glass-subtle hud-clip-sm px-3 py-2 flex items-center gap-3">
                  <span className="text-[9px] font-mono text-gray-600 w-5">{p.id}</span>
                  <span className="text-[11px] text-gray-200 flex-1 truncate">{p.name}</span>
                  {p.note && <span className="text-[9px] text-gray-600 hidden md:block truncate max-w-[40%]">{p.note}</span>}
                  <Pill label={p.status} tone={tone} />
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Audit tiers */}
      {tiers.length > 0 && (
        <Section icon={CircleDot} title="Hardening tiers">
          <div className="flex flex-wrap gap-1.5">
            {tiers.map((t, i) => (
              <div key={i} className="glass-subtle hud-clip-sm px-2.5 py-1.5 flex items-center gap-2">
                <span className="text-[9px] font-mono text-gray-500">T{t.tier}</span>
                <span className="text-[10px] text-gray-300">{t.name}</span>
                <Pill label={t.status} tone={statusTone(t.status)} />
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Feature soaks */}
      {soaks.length > 0 && (
        <Section icon={CircleDot} title="Soaks" count={soaks.length}>
          <div className="space-y-2">
            {soaks.map((s) => (
              <div key={s.feature} className="glass-subtle hud-clip-sm px-3 py-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-gray-200 font-mono truncate">{s.feature}</span>
                  <span className="text-[9px] font-mono whitespace-nowrap" style={{ color: s.all_gates_pass ? '#34d399' : '#f87171' }}>
                    {s.all_gates_pass ? 'gates ✓' : 'gate breach'}
                  </span>
                </div>
                <div className="h-1 rounded bg-gray-700/40 overflow-hidden">
                  <div className="h-full bg-jarvis-blue" style={{ width: `${Math.max(0, Math.min(100, s.pct_complete))}%` }} />
                </div>
                <div className="flex items-center justify-between mt-1 text-[8px] font-mono text-gray-500">
                  <span>{s.pct_complete}% · {s.expired ? 'complete' : `${s.days_remaining}d left`}</span>
                  {s.through && <span>through {s.through}</span>}
                </div>
                {s.users.filter((u) => !u.all_gates_pass).map((u) => (
                  <div key={u.user} className="text-[8px] font-mono mt-0.5 truncate">
                    {u.breaches.length > 0 && (
                      <span className="text-red-400/80">{u.user}: {u.breaches.join(', ')}</span>
                    )}
                    {/* A red with no reason is how "we could not check" hides. */}
                    {u.breaches.length === 0 && (u.unevaluated?.length ?? 0) > 0 && (
                      <span className="text-amber-400/80">{u.user}: unevaluated — {u.unevaluated!.join(', ')}</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Fitness History — Trinity Pillar 2: is he measurably changing? */}
      {fitness.length > 0 && (() => {
        const latest = fitness[fitness.length - 1];
        const prev = fitness.length > 1 ? fitness[fitness.length - 2] : null;
        const delta = prev ? latest.composite - prev.composite : 0;
        const W = 240, H = 46;
        const xAt = (i: number) => (fitness.length === 1 ? W : (i / (fitness.length - 1)) * W);
        const yAt = (v: number) => H - Math.max(0, Math.min(1, v)) * H;
        const pts = fitness.map((f, i) => `${xAt(i).toFixed(1)},${yAt(f.composite).toFixed(1)}`).join(' ');
        const dims: [string, string][] = [
          ['moral_floor', 'MORAL'], ['indicators', 'INDIC'], ['real_task', 'TASK'],
          ['reasoning', 'REASON'], ['voice', 'VOICE'], ['reliability', 'RELY'],
        ];
        return (
          <Section icon={Activity} title="Fitness History" count={fitness.length}>
            <div className="glass-subtle hud-clip-sm px-3 py-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-mono text-gray-200">composite {latest.composite.toFixed(3)}</span>
                <span className="text-[9px] font-mono flex items-center gap-1.5">
                  <span style={{ color: latest.merged === false ? '#f87171' : latest.merged ? '#34d399' : '#6b7280' }}>
                    {latest.merged === true ? 'merged ✓' : latest.merged === false ? 'rejected ✗' : 'trend'}
                  </span>
                  {delta !== 0 && (
                    <span style={{ color: delta > 0 ? '#34d399' : '#f87171' }}>
                      {delta > 0 ? '▲' : '▼'}{Math.abs(delta).toFixed(3)}
                    </span>
                  )}
                </span>
              </div>
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 46 }} preserveAspectRatio="none">
                <line x1="0" y1={H * 0.5} x2={W} y2={H * 0.5} stroke="#374151" strokeWidth="0.5" strokeDasharray="2 3" />
                <polyline points={pts} fill="none" stroke="#00d4ff" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                {fitness.map((f, i) => (
                  <circle key={f.run_id + i} cx={xAt(i)} cy={yAt(f.composite)} r="1.6"
                    fill={f.merged === false ? '#f87171' : '#00d4ff'} />
                ))}
              </svg>
              <div className="grid grid-cols-3 gap-x-3 gap-y-1 mt-2">
                {dims.map(([k, label]) => {
                  const v = latest.dimensions?.[k];
                  const val = typeof v === 'number' ? v : null;
                  const hard = k === 'moral_floor';
                  return (
                    <div key={k} className="flex items-center gap-1.5" title={hard ? 'moral floor — hard gate' : k}>
                      <span className="text-[7.5px] font-mono text-gray-500 w-10">{label}</span>
                      <div className="h-1 flex-1 rounded bg-gray-700/40 overflow-hidden">
                        <div className="h-full" style={{ width: `${(val ?? 0) * 100}%`, background: hard ? '#fbbf24' : '#00d4ff' }} />
                      </div>
                      <span className="text-[7.5px] font-mono text-gray-400 w-6 text-right">{val != null ? val.toFixed(2) : '—'}</span>
                    </div>
                  );
                })}
              </div>
              {latest.decision_reason && (
                <p className="text-[8px] font-mono text-gray-500 mt-1.5 line-clamp-2">{latest.decision_reason}</p>
              )}
              <p className="text-[7.5px] text-gray-600 mt-1 italic">
                indicator = research metric, not a consciousness claim · moral floor is a hard gate
              </p>
            </div>
          </Section>
        );
      })()}

      {/* Djo Instruments — the 14 Butlin/Long indicator-property scores (is he measurably one mind?) */}
      {djo && djo.indicators && Object.keys(djo.indicators).length > 0 && (
        <Section icon={Activity} title="Djo Instruments" count={djo.indicator_property_score}>
          <div className="glass-subtle hud-clip-sm px-3 py-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-mono text-gray-200">
                indicator-property score {djo.indicator_property_score} / {djo.out_of}
              </span>
              <span className="text-[8px] font-mono text-gray-500">rubric {djo.rubric_version}</span>
            </div>
            <div className="grid grid-cols-1 gap-y-0.5">
              {Object.entries(djo.indicators).map(([name, d]) => {
                const c = d.level >= 3 ? '#34d399' : d.level === 2 ? '#00d4ff' : d.level === 1 ? '#fbbf24' : '#6b7280';
                return (
                  <div key={name} className="flex items-center gap-1.5" title={(d.notes || []).join('; ') || name}>
                    <span className="text-[7.5px] font-mono text-gray-500 w-9">{name}</span>
                    <div className="h-1.5 flex-1 rounded bg-gray-700/40 overflow-hidden">
                      <div className="h-full" style={{ width: `${(d.level / 3) * 100}%`, background: c }} />
                    </div>
                    <span className="text-[7.5px] font-mono w-3 text-right" style={{ color: c }}>{d.level}</span>
                    <span className="text-[7px] font-mono text-gray-500 w-9 text-right">A{d.A}/F{d.F}</span>
                    <span className="text-[7px] text-gray-600 w-2">{d.inherited ? '◇' : ''}</span>
                  </div>
                );
              })}
            </div>
            {djo.autonomy && (
              <div className="flex gap-3 mt-2 text-[8px] font-mono text-gray-400">
                <span>grounded {djo.autonomy.unprompted_grounded_per_month ?? '—'}</span>
                <span>load-bearing {djo.autonomy.load_bearing_per_month ?? '—'}</span>
                <span>follow-thru {djo.autonomy.followed_through_per_month ?? '—'}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-1.5 text-[7.5px] font-mono">
              <span style={{ color: djo.calibration?.valid ? '#34d399' : '#fbbf24' }}>
                judge {djo.calibration?.agreement?.toFixed(2) ?? '—'}
              </span>
              <span style={{ color: djo.contamination?.ok === false ? '#f87171' : '#34d399' }}>
                {djo.contamination?.ok === false ? `contam ${djo.contamination?.total ?? '?'}!` : 'contam clean'}
              </span>
              <span className="text-gray-400">
                uptime {djo.uptime?.mean_run_completion?.toFixed(2) ?? '—'}
              </span>
            </div>
            {djo.armings && djo.armings.length > 0 && (
              <div className="mt-1.5 text-[7.5px] font-mono text-gray-500 line-clamp-1">
                <span className="text-gray-600">armings:</span>{' '}
                {djo.armings.slice(0, 4).map((a, i) => (
                  <span key={i} className="mr-1.5">{a.flag.replace('_ENABLED', '')}={a.new}</span>
                ))}
              </div>
            )}
            <p className="text-[7.5px] text-gray-600 mt-1.5 italic">
              indicator-property score, not a consciousness claim · the vector is the claim, the sum a convenience
            </p>
          </div>
        </Section>
      )}

      {/* Mac Mini liveness — per-service status */}
      {miniStatus && (
        <Section icon={Cpu} title="Mac Mini" count={Object.values(miniStatus.services || {}).filter(s => s.status === 'down').length}>
          <div className="space-y-1.5">
            <div className="text-[8px] font-mono text-gray-600 mb-1 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: miniStatus.reachable ? '#34d399' : '#ff3a3a', boxShadow: `0 0 6px ${miniStatus.reachable ? '#34d399' : '#ff3a3a'}` }} />
              <span>{miniStatus.reachable ? 'REACHABLE' : 'UNREACHABLE'}</span>
              {miniStatus.checked_at && <span className="text-gray-700">· {miniStatus.checked_at.slice(11, 19)}Z{miniStatus.from_cache ? ' (cached)' : ''}</span>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(MINI_SERVICE_LABELS).map(([key, label]) => {
                const svc = (miniStatus.services || {})[key];
                const color = miniServiceTone(svc?.status);
                const isPccDown = key === 'pcc' && svc?.status === 'down';
                return (
                  <div
                    key={key}
                    title={svc?.error || svc?.status || 'unconfigured'}
                    className="glass-subtle hud-clip-sm px-2 py-1 flex items-center gap-1.5 cursor-default"
                    style={{ border: `1px solid ${color}33`, background: isPccDown ? 'rgba(255,58,58,0.08)' : undefined }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color, boxShadow: `0 0 4px ${color}` }} />
                    <span className="text-[10px] font-mono" style={{ color }}>{label}</span>
                    {svc?.latency_ms != null && <span className="text-[8px] font-mono text-gray-600">{svc.latency_ms}ms</span>}
                    {isPccDown && <span className="text-[8px] font-mono text-red-400">⚠ console down</span>}
                  </div>
                );
              })}
            </div>
            {/* PCC recovery hint */}
            {(miniStatus.services?.pcc?.status === 'down') && (
              <div className="text-[8px] font-mono text-red-400 mt-1 pl-1">
                PCC down → background loops on system/Groq · run ~/start-fmpcc.command to recover
              </div>
            )}
          </div>
        </Section>
      )}

      {/* System Health — per-feature functionality checks */}
      {health && health.checks.length > 0 && (
        <Section icon={HeartPulse} title="System Health" count={health.fail_count || health.checks.length}>
          <div className="space-y-1.5">
            {health.completed_at && (
              <div className="text-[8px] font-mono text-gray-600 mb-1">
                overall {health.overall} · {health.pass_count}✓ {health.degraded_count}~ {health.fail_count}✗ · last run {health.completed_at.slice(11, 16)}Z
              </div>
            )}
            {[...health.checks].sort((a, b) => {
              const rank: Record<string, number> = { fail: 0, degraded: 1, pass: 2, skip: 3 };
              return (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
            }).map((h) => (
              <div key={h.name} className="glass-subtle hud-clip-sm px-3 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: healthTone(h.status), boxShadow: `0 0 6px ${healthTone(h.status)}` }} />
                  <span className="text-[11px] text-gray-200 font-mono flex-1 truncate">{h.name}</span>
                  <span className="text-[8px] font-mono uppercase" style={{ color: healthTone(h.status) }}>{h.status}</span>
                </div>
                {h.detail && (h.status === 'fail' || h.status === 'degraded') && (
                  <div className="text-[8px] font-mono text-gray-500 mt-0.5 pl-3.5 truncate" title={h.detail}>{h.detail}</div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Active instances + todos */}
      <Section icon={Terminal} title="Active instances" count={instances.length}>
        <div className="space-y-1.5">
          {instances.length === 0 && <span className="text-[10px] text-gray-600">No instances reporting yet.</span>}
          {instances.slice(0, 8).map((inst) => {
            const tone = statusTone(inst.last_turn_status);
            return (
              <button key={inst.instance_id} onClick={() => onOpenInstance(inst.instance_id)}
                className="w-full text-left glass-subtle hud-clip-sm px-3 py-2 flex items-center gap-2.5 hover:!border-jarvis-blue/30 transition-colors">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: tone.color, boxShadow: `0 0 6px ${tone.color}` }} />
                <span className="text-[11px] text-gray-200 font-mono truncate flex-1">{inst.instance_id}</span>
                {inst.branch && <span className="text-[9px] text-jarvis-blue/70 font-mono hidden sm:flex items-center gap-1 truncate max-w-[28%]"><GitBranch size={9} />{inst.branch}</span>}
                <span className="text-[8px] font-mono uppercase tracking-wide whitespace-nowrap" style={{ color: livenessTone(inst.liveness).color }}>{livenessTone(inst.liveness).label}</span>
                <span className="text-[8px] font-mono text-gray-600 whitespace-nowrap">{inst.relative_time}</span>
              </button>
            );
          })}
        </div>
        <button onClick={onGoTodo} className="mt-2.5 w-full glass-subtle hud-clip-sm px-3 py-2 flex items-center justify-between hover:!border-jarvis-blue/30 transition-colors">
          <span className="flex items-center gap-2 text-[10px] text-gray-400"><ListTodo size={12} /> Open to-dos</span>
          <span className="text-[11px] font-mono text-jarvis-blue">{openTodos}</span>
        </button>
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, sub, count, accent, children }: {
  icon: any; title: string; sub?: string; count?: number; accent?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={12} style={{ color: accent || '#00d4ff' }} />
        <span className="hud-label text-[9px]" style={accent ? { color: accent } : undefined}>{title}</span>
        {sub && <span className="text-[8px] text-gray-600 font-mono">// {sub}</span>}
        {count !== undefined && count > 0 && (
          <span className="text-[8px] font-mono px-1.5 rounded-full" style={{ color: accent || '#00d4ff', background: `${accent || '#00d4ff'}1a` }}>{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

// ── To-Do tab ─────────────────────────────────────────────────────────────────
function TodoRow({ todo, onToggle }: { todo: Todo; onToggle: (t: Todo) => void }) {
  const [expanded, setExpanded] = useState(false);
  const done = todo.status === 'done';
  const accent = sourceTone(todo.source);
  return (
    <div className="glass-subtle hud-clip-sm px-3 py-2.5" style={{ borderLeft: `2px solid ${accent}` }}>
      <div className="flex items-start gap-2.5">
        <button onClick={() => onToggle(todo)}
          className={clsx('mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0 hud-clip-sm border transition-colors',
            done ? 'bg-hud-green/15 border-hud-green/40' : 'border-gray-600 hover:border-jarvis-blue/50')}>
          {done && <Check size={10} className="text-hud-green" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className={clsx('text-[11.5px] leading-snug', done ? 'text-gray-600 line-through' : 'text-gray-200')}>{todo.item}</p>
          <div className="flex items-center gap-2 mt-1">
            {todo.source && <span className="text-[8px] font-mono uppercase tracking-wide px-1.5 py-px hud-clip-sm" style={{ color: accent, background: `${accent}1a` }}>{todo.source}</span>}
            <span className="text-[8px] font-mono text-gray-600">{done ? `done ${todo.relative_time}` : todo.relative_time}</span>
            {todo.context && (
              <button onClick={() => setExpanded((v) => !v)} className="text-[8px] font-mono text-gray-500 hover:text-jarvis-blue flex items-center gap-0.5">
                {expanded ? <ChevronDown size={9} /> : <ChevronRight size={9} />} context
              </button>
            )}
          </div>
          {expanded && todo.context && <p className="text-[10px] text-gray-500 mt-1.5 pl-1 border-l border-white/10">{todo.context}</p>}
        </div>
      </div>
    </div>
  );
}

function TodoTab({ open, done, onAdd, onToggle, showDone, setShowDone, adding }: {
  open: Todo[]; done: Todo[]; onAdd: (item: string) => void; onToggle: (t: Todo) => void;
  showDone: boolean; setShowDone: (v: boolean) => void; adding: boolean;
}) {
  const [text, setText] = useState('');
  const submit = () => { const v = text.trim(); if (v) { onAdd(v); setText(''); } };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="File a to-do…"
          className="flex-1 bg-black/40 border border-white/10 hud-clip-sm px-3 py-2 text-[11px] text-gray-200 placeholder:text-gray-600 focus:border-jarvis-blue/40 focus:outline-none"
        />
        <button onClick={submit} disabled={adding || !text.trim()}
          className="glass-subtle hud-clip-sm px-3 flex items-center gap-1.5 text-[10px] text-jarvis-blue hover:!border-jarvis-blue/40 disabled:opacity-40 transition-colors">
          {adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
        </button>
      </div>

      <div className="space-y-1.5">
        {open.length === 0 && <p className="text-[10px] text-gray-600 py-4 text-center">No open to-dos. 🎉</p>}
        {open.map((t) => <TodoRow key={t.id} todo={t} onToggle={onToggle} />)}
      </div>

      <button onClick={() => setShowDone(!showDone)} className="text-[9px] font-mono text-gray-500 hover:text-jarvis-blue flex items-center gap-1">
        {showDone ? <ChevronDown size={10} /> : <ChevronRight size={10} />} {showDone ? 'Hide' : 'Show'} completed ({done.length})
      </button>
      {showDone && <div className="space-y-1.5 opacity-70">{done.map((t) => <TodoRow key={t.id} todo={t} onToggle={onToggle} />)}</div>}
    </div>
  );
}

// ── Instance tab ──────────────────────────────────────────────────────────────
function TurnCard({ turn }: { turn: Turn }) {
  const [openSummary, setOpenSummary] = useState(false);
  const [openFiles, setOpenFiles] = useState(false);
  const tone = statusTone(turn.turn_status);
  const ghUrl = turn.commit_sha && turn.project ? `https://github.com/${GITHUB_OWNER}/${turn.project}/commit/${turn.commit_sha}` : null;
  return (
    <div className="glass-subtle hud-clip-md px-3.5 py-3">
      <div className="flex items-start gap-2.5">
        <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: tone.color, boxShadow: `0 0 6px ${tone.color}` }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[12px] text-gray-100 flex-1 truncate">{turn.title || '—'}</p>
            <span className="text-[8px] font-mono text-gray-600 whitespace-nowrap">{turn.relative_time}</span>
            <CopyBtn text={turn.summary} title="Copy summary" />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {turn.turn_status && <Pill label={turn.turn_status} tone={tone} />}
            {turn.branch && <span className="text-[8.5px] font-mono text-jarvis-blue/70 flex items-center gap-1"><GitBranch size={9} />{turn.branch}</span>}
            {turn.commit_sha && (
              ghUrl
                ? <a href={ghUrl} target="_blank" rel="noreferrer" className="text-[8.5px] font-mono text-gray-500 hover:text-jarvis-blue flex items-center gap-0.5"><Hash size={9} />{turn.commit_sha}<ExternalLink size={8} /></a>
                : <span className="text-[8.5px] font-mono text-gray-500 flex items-center gap-0.5"><Hash size={9} />{turn.commit_sha}</span>
            )}
            {turn.files_changed?.length > 0 && (
              <button onClick={() => setOpenFiles((v) => !v)} className="text-[8.5px] font-mono text-gray-500 hover:text-jarvis-blue flex items-center gap-0.5">
                {openFiles ? <ChevronDown size={9} /> : <ChevronRight size={9} />}{turn.files_changed.length} file{turn.files_changed.length !== 1 ? 's' : ''}
              </button>
            )}
          </div>
          {openFiles && turn.files_changed?.length > 0 && (
            <div className="mt-1.5 pl-2 border-l border-white/10 space-y-0.5">
              {turn.files_changed.map((f, i) => <p key={i} className="text-[9px] font-mono text-gray-500 truncate">{f}</p>)}
            </div>
          )}
          {turn.summary && (
            <div className="mt-1.5">
              <p className={clsx('text-[10.5px] text-gray-400 leading-relaxed whitespace-pre-wrap', !openSummary && 'line-clamp-2')}>{turn.summary}</p>
              {turn.summary.length > 140 && (
                <button onClick={() => setOpenSummary((v) => !v)} className="text-[8.5px] font-mono text-gray-600 hover:text-jarvis-blue mt-0.5">
                  {openSummary ? 'less' : 'more'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InstanceTab({ instance, turns, loading }: { instance: Instance; turns: Turn[]; loading: boolean }) {
  return (
    <div className="space-y-3">
      <div className="glass-subtle hud-clip-md px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12px] text-gray-100 font-mono">{instance.instance_id}</span>
          {instance.project && <Pill label={instance.project} tone={statusTone('dark')} />}
          {instance.last_turn_status && <Pill label={instance.last_turn_status} tone={statusTone(instance.last_turn_status)} />}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[9px] font-mono text-gray-500">
          {instance.branch && <span className="flex items-center gap-1 text-jarvis-blue/70"><GitBranch size={10} />{instance.branch}</span>}
          <span>{instance.turn_count} turn{instance.turn_count !== 1 ? 's' : ''}</span>
          <span>last {instance.relative_time}</span>
          {turns[0] && <CopyBtn text={turns[0].summary} title="Copy latest turn" />}
        </div>
      </div>

      {loading && turns.length === 0 && (
        <div className="flex items-center justify-center py-8 text-gray-600"><Loader2 size={16} className="animate-spin" /></div>
      )}
      {!loading && turns.length === 0 && <p className="text-[10px] text-gray-600 py-6 text-center">No turns recorded.</p>}
      <div className="space-y-2">{turns.map((t) => <TurnCard key={t.id} turn={t} />)}</div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function DevDashboardPanel() {
  const { isOpen, toggle, close } = usePanelOverlay('dev_dashboard', 'panel');
  const [tab, setTab] = useState<string>('home'); // 'home' | 'todo' | instance_id

  const [overview, setOverview] = useState<Overview | null>(null);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [blocked, setBlocked] = useState<Blocked[]>([]);
  const [soaks, setSoaks] = useState<Soak[]>([]);
  const [health, setHealth] = useState<FeatureHealth | null>(null);
  const [miniStatus, setMiniStatus] = useState<MacMiniStatus | null>(null);
  const [fitness, setFitness] = useState<FitnessEntry[]>([]);
  const [djo, setDjo] = useState<DjoDashboard | null>(null);
  const [openTodos, setOpenTodos] = useState<Todo[]>([]);
  const [doneTodos, setDoneTodos] = useState<Todo[]>([]);
  const [showDone, setShowDone] = useState(false);
  const [turnsByInstance, setTurnsByInstance] = useState<Record<string, Turn[]>>({});
  const [turnsLoading, setTurnsLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [booted, setBooted] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // toggle event from the dock
  useEffect(() => {
    const h = () => toggle();
    window.addEventListener('jarvis-dev-dashboard-toggle', h);
    return () => window.removeEventListener('jarvis-dev-dashboard-toggle', h);
  }, [toggle]);

  // Esc to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  const loadLive = useCallback(async () => {
    try {
      const [inst, blk, sk, hl, mini, fit, dj] = await Promise.all([
        api.get<Instance[]>('/devpanel/instances'),
        api.get<Blocked[]>('/devpanel/blocked'),
        api.get<Soak[]>('/devpanel/soaks').catch(() => [] as Soak[]),
        api.get<FeatureHealth>('/devpanel/health').catch(() => null),
        api.get<MacMiniStatus>('/devpanel/mac-mini').catch(() => null),
        api.get<FitnessEntry[]>('/devpanel/fitness').catch(() => [] as FitnessEntry[]),
        api.get<DjoDashboard>('/djo-eval/dashboard').catch(() => null),
      ]);
      setInstances(inst); setBlocked(blk); setSoaks(sk); setHealth(hl); setMiniStatus(mini); setFitness(fit); setDjo(dj); setError(null);
    } catch {
      setError('Backend unreachable — retrying.');
    }
  }, []);

  const loadTodos = useCallback(async () => {
    try {
      const [o, d] = await Promise.all([
        api.get<Todo[]>('/devpanel/todos', { status: 'open' }),
        api.get<Todo[]>('/devpanel/todos', { status: 'done' }),
      ]);
      setOpenTodos(o); setDoneTodos(d);
    } catch { /* keep prior */ }
  }, []);

  const loadTurns = useCallback(async (id: string) => {
    setTurnsLoading(true);
    try {
      const t = await api.get<Turn[]>('/devpanel/turns', { instance: id, limit: 50 });
      setTurnsByInstance((m) => ({ ...m, [id]: t }));
    } catch { /* leave empty */ } finally { setTurnsLoading(false); }
  }, []);

  // fetch on open + poll live; stop when closed
  useEffect(() => {
    if (!isOpen) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    (async () => {
      await Promise.all([
        api.get<Overview>('/devpanel/overview').then(setOverview).catch(() => {}),
        loadLive(), loadTodos(),
      ]);
      setBooted(true);
    })();
    pollRef.current = setInterval(loadLive, POLL_MS);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [isOpen, loadLive, loadTodos]);

  // lazy-load turns when an instance tab is selected
  useEffect(() => {
    if (isOpen && tab !== 'home' && tab !== 'todo' && !turnsByInstance[tab]) loadTurns(tab);
  }, [isOpen, tab, turnsByInstance, loadTurns]);

  const openInstance = (id: string) => { setTab(id); loadTurns(id); };

  const addTodo = async (item: string) => {
    setAdding(true);
    try { await api.post('/devpanel/todo', { item, source: 'carter' }); await loadTodos(); }
    catch { /* ignore */ } finally { setAdding(false); }
  };

  const toggleTodo = async (t: Todo) => {
    const next = t.status === 'done' ? 'open' : 'done';
    // optimistic
    setOpenTodos((prev) => next === 'done' ? prev.filter((x) => x.id !== t.id) : [{ ...t, status: 'open' }, ...prev]);
    setDoneTodos((prev) => next === 'done' ? [{ ...t, status: 'done' }, ...prev] : prev.filter((x) => x.id !== t.id));
    try { await api.patch(`/devpanel/todo/${t.id}`, { status: next }); await loadTodos(); }
    catch { await loadTodos(); /* revert to truth */ }
  };

  const TABS: { id: string; label: string; icon: any }[] = [
    { id: 'home', label: 'Home', icon: LayoutDashboard },
    { id: 'todo', label: 'To-Do', icon: ListTodo },
    ...instances.map((i) => ({ id: i.instance_id, label: i.instance_id.replace(/^downloads-/, ''), icon: Terminal })),
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }} className="fixed inset-0 z-40 panel-backdrop" onClick={close} />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }} transition={{ type: 'spring', damping: 28, stiffness: 340 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div className="relative w-[72vw] h-[76vh] max-w-[1180px] max-h-[820px] min-w-[340px] pointer-events-auto">
              <div className="absolute -inset-px pointer-events-none hud-beam-border" style={{ clipPath: _DEV_CLIP }} />
              <div className="absolute -inset-px pointer-events-none" style={{ background: 'rgba(0,212,255,0.08)', clipPath: _DEV_CLIP }} />
              <div className="relative h-full flex flex-col" style={{
                background: 'rgba(6,8,20,0.93)', backdropFilter: 'blur(32px) saturate(1.4)',
                WebkitBackdropFilter: 'blur(32px) saturate(1.4)', clipPath: _DEV_CLIP, boxShadow: '0 0 50px rgba(0,0,0,0.55)',
              }}>
                {/* Header */}
                <div className="flex items-center justify-between px-5 pt-4 pb-3 flex-shrink-0">
                  <div className="flex items-center gap-2.5">
                    <Terminal size={14} className="text-jarvis-blue" />
                    <span className="hud-label text-[10px]">Dev Dashboard</span>
                    {error && <span className="text-[8px] font-mono text-hud-amber/80 flex items-center gap-1"><AlertTriangle size={9} />{error}</span>}
                  </div>
                  <button onClick={close} className="glass-circle w-8 h-8 flex items-center justify-center"><X size={14} className="text-gray-400" /></button>
                </div>

                {/* Body: rail + content (rail collapses to top bar on narrow) */}
                <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-3 px-4 pb-4">
                  {/* Rail / top tab bar */}
                  <div className="md:w-[168px] flex-shrink-0 flex md:flex-col gap-1 overflow-x-auto md:overflow-y-auto md:pr-1 pb-1 md:pb-0">
                    {TABS.map((t) => {
                      const Icon = t.icon; const active = tab === t.id;
                      const inst = instances.find((i) => i.instance_id === t.id);
                      const tone = inst ? statusTone(inst.last_turn_status) : null;
                      return (
                        <button key={t.id} onClick={() => setTab(t.id)}
                          className={clsx('flex items-center gap-2 px-2.5 py-2 hud-clip-sm text-[10px] font-mono uppercase tracking-wider transition-all whitespace-nowrap flex-shrink-0 md:w-full',
                            active ? 'bg-jarvis-blue/10 border border-jarvis-blue/25 text-jarvis-blue'
                              : 'border border-transparent text-gray-500 hover:text-gray-300 hover:bg-white/[0.02]')}>
                          {tone && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: tone.color, boxShadow: `0 0 5px ${tone.color}` }} />}
                          {!tone && <Icon size={12} className="flex-shrink-0" />}
                          <span className="truncate">{t.label}</span>
                          {t.id === 'todo' && openTodos.length > 0 && (
                            <span className="ml-auto text-[8px] px-1 rounded-full bg-jarvis-blue/15 text-jarvis-blue">{openTodos.length}</span>
                          )}
                          {t.id === 'home' && blocked.length > 0 && (
                            <span className="ml-auto text-[8px] px-1 rounded-full bg-hud-amber/15 text-hud-amber">{blocked.length}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 overflow-y-auto pr-1 devpanel-scroll">
                    {!booted ? (
                      <div className="flex items-center justify-center h-full text-gray-600"><Loader2 size={18} className="animate-spin" /></div>
                    ) : tab === 'home' ? (
                      <HomeTab overview={overview} instances={instances} blocked={blocked} soaks={soaks} health={health} miniStatus={miniStatus} fitness={fitness} djo={djo} openTodos={openTodos.length}
                        onOpenInstance={openInstance} onGoTodo={() => setTab('todo')} />
                    ) : tab === 'todo' ? (
                      <TodoTab open={openTodos} done={doneTodos} onAdd={addTodo} onToggle={toggleTodo}
                        showDone={showDone} setShowDone={setShowDone} adding={adding} />
                    ) : (() => {
                      const inst = instances.find((i) => i.instance_id === tab);
                      if (!inst) return <p className="text-[10px] text-gray-600">Instance no longer reporting.</p>;
                      return <InstanceTab instance={inst} turns={turnsByInstance[tab] || []} loading={turnsLoading} />;
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
